import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  FormGroupDirective,
  NgForm,
  Validators,
} from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { FlamingoError } from '@flamingo/service/flamingo-error.handler';
import { FlamingoHttpService } from '@flamingo/service/flamingo-http.service';
import { FlamingoMessageService } from '@flamingo/service/flamingo-message.service';
import { fuseAnimations } from '@fuse/animations';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import * as AutoNumeric from 'autonumeric';
import * as moment from 'moment';
import { Moment } from 'moment';
import { fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, finalize, tap } from 'rxjs/operators';
import { ClientView } from '../../../../../../../../core/model/client-view.model';
import {
  Attachment,
  AttachmentType,
  Installment,
  Invoice,
  InvoiceBidsPrice,
} from '../../../../../../../../core/model/invoice.model';
import { TransferType } from '../../../../../../../../core/model/transfer-type.model';
import { TaxCodeDataService } from '../../../../../../../../core/service/tax-code.data.service';
import { taxCodeValidator } from '../../../../../../../../core/validator/tax-code.validator';
import { MainService } from '../../../../../../main.service';
import { ClientService } from '../../../client/client.service';
import { InvoiceService } from '../../invoice.service';
import { locale as english } from './i18n/en';
import { locale as italian } from './i18n/it';

class CustomErrorMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return (control.dirty || control.touched) && control.invalid;
  }
}

@Component({
  selector: 'app-new-invoice-dialog',
  templateUrl: './new-invoice-dialog.component.html',
  styleUrls: ['./new-invoice-dialog.component.scss'],
  animations: [fuseAnimations],
  encapsulation: ViewEncapsulation.None,
})
export class NewInvoiceDialogComponent implements OnInit, AfterViewInit {

  @ViewChild('stepper', { static: true }) stepper: MatStepper;

  CustomErrorMatcher = new CustomErrorMatcher();

  clientForm: UntypedFormGroup;
  debtorForm: UntypedFormGroup;
  invoiceForm: UntypedFormGroup;
  auditionForm: UntypedFormGroup;
  otherForm: UntypedFormGroup;

  invoiceFileName: string;
  invoiceFile;
  today = moment();
  theDayAfterTomorrow = moment().add(2, 'days');
  averagePaymentDelayTmp = null;

  @ViewChild('minimumBidRef', { static: true }) minimumBidRef: ElementRef;
  @ViewChild('fileInputOtherDoc') fileInputOtherDoc: ElementRef;

  currencyField: any;

  minimumBidDiscount = 0;

  priceMin: number;
  priceMax: number;
  priceSuggested: number;

  creatingInvoice = false;
  uploadingAttachment = false;

  clients: ClientView[] = [];
  filteredClients: ClientView[] = [];

  userMadeAChoice = false;
  isNew = false;

  uuid: string;
  invoiceFiles: Attachment[] = [];
  attachmentForm: UntypedFormGroup;
  AttachmentType = AttachmentType;
  invoiceCreateOrUpdateErrorMessage: string = null;

  invoice: Invoice;

  showDebtorContactData: boolean = false;

  invoiceInstallments: Installment[] = [];
  displayedColumns: string[] = ['installmentNumber', 'amount', 'dueDate'];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: NewInvoiceDialogModel,
    public taxCodeDataService: TaxCodeDataService,
    private _clientService: ClientService,
    private _dialogRef: MatDialogRef<NewInvoiceDialogComponent>,
    private _formBuilder: UntypedFormBuilder,
    private _httpService: FlamingoHttpService,
    private _invoiceService: InvoiceService,
    private _mainService: MainService,
    private _messageService: FlamingoMessageService,
    private _translationLoader: FuseTranslationLoaderService,
    private _translateService: TranslateService,
  ) {
    this._translationLoader.loadTranslations(italian, english);
  }

  ngOnInit(): void {
    if (this.data.invoice) {
      this.invoice = this.data.invoice;

      if (this.invoice.attachments && Array.isArray(this.invoice.attachments)) {
        this.invoice.attachments.map(attachment => this.invoiceFiles.push(attachment));
      }

      if (this.invoice.installments && Array.isArray(this.invoice.installments)) {
        this.invoiceInstallments = this.invoice.installments;
      }
    }

    if (this.data.action === 'edit' && this.invoice) {
      this.uuid = this.invoice.uuid;

      if (this.data.attachment) {
        this.stepper.selectedIndex = 3;
      }
    }

    this.userMadeAChoice = false;
    this.isNew = false;

    this.clientForm = this._formBuilder.group({
      clientPicker: new UntypedFormControl({
        value: '',
        disabled: this.invoice,
      }),
    });

    this.initDebtorForm();
    this.initInvoiceForm();
    this.initAuditionForm();

    this.otherForm = this._formBuilder.group({});

    this.attachmentForm = this._formBuilder.group({
      attachmentType: [null],
    });

    fromEvent(this.minimumBidRef.nativeElement, 'change')
      .pipe(
        debounceTime(100),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        this.minimumBidDiscount = this.calculateMinimumBid(
          this.minimumBidRef.nativeElement.valueAsNumber,
        );
      });
  }

  calculateMinimumBid(bid?: number): number {
    if (bid) {
      return (
        ((this.auditionForm.get('invoiceAmount').value - bid) /
          this.auditionForm.get('invoiceAmount').value) *
        100
      );
    }
    return (
      ((this.auditionForm.get('invoiceAmount').value - this.auditionForm.get('minimumBid').value) /
        this.auditionForm.get('invoiceAmount').value) *
      100
    );
  }

  initDebtorForm(): void {
    if (this.data.action === 'add') {
      this._clientService.list(0, 999)
        .subscribe(res => {
          this.clients = res.data;
          this.filteredClients = this.clients;
          this.getShowDebtorContactData(this.data.client);
        });
    }

    this.debtorForm = this._formBuilder.group({
      businessName: new UntypedFormControl(
        {
          value: this.invoice ? this.invoice.debtorBusinessName : null,
          disabled: this.invoice,
        },
        Validators.required,
      ),
      taxCode: new UntypedFormControl(
        {
          value: this.invoice ? this.invoice.debtorTaxCode : null,
          disabled: this.invoice,
        },
        [Validators.required, taxCodeValidator],
      ),
      taxCodePrefix: new UntypedFormControl(
        {
          value: this.invoice ? this.invoice.debtorTaxCodePrefix : null,
          disabled: this.invoice,
        },
        Validators.required,
      ),
    });

    if (this.data.action === 'edit') {
      this.getShowDebtorContactData(this.data.client);
    }

    this.clientForm.get('clientPicker')
      .valueChanges
      .subscribe(value => {
        if (value) {
          this.filteredClients = this.autocompleteFilterDebtors(value);
        } else {
          this.filteredClients = this.autocompleteFilterDebtors('');
        }
      });

    this.debtorForm
      .get('taxCodePrefix')
      .valueChanges
      .pipe(
        tap(val => {
          this.debtorForm.get('taxCode')
            .setValidators([Validators.required]);
          if (val === 'IT') {
            this.debtorForm.get('taxCode')
              .setValidators([Validators.required, taxCodeValidator]);
          }
          this.debtorForm.get('taxCode')
            .updateValueAndValidity();
        }),
      )
      .subscribe();
  }

  initInvoiceForm(): void {
    this.invoiceForm = this._formBuilder.group(
      {
        number: new UntypedFormControl({
          value: this.invoice ? this.invoice.number : null,
          disabled: this.isInvoiceFromXml(),
        }, Validators.required),
        issueDate: new UntypedFormControl({
          value: this.invoice ? this.invoice.issueDate : null,
          disabled: this.isInvoiceFromXml(),
        }, Validators.required),
        dueDate: new UntypedFormControl({
          value: this.invoice ? this.invoice.dueDate : null,
          disabled: this.isInvoiceFromXml(),
        }, Validators.required),
        amount: new UntypedFormControl({
          value: this.invoice ? this.invoice.amount : null,
          disabled: this.isInvoiceFromXml(),
        }, Validators.required),
        file: [null],
      },
      { updateOn: 'blur' },
    );
  }

  initAuditionForm(): void {
    this.auditionForm = this._formBuilder.group({
      averagePaymentDelay: [
        this.invoice ? this.invoice.averagePaymentDelay : this.averagePaymentDelayTmp || null,
        [Validators.required, Validators.pattern('[0-9 ]+')],
      ],
      invoiceAmount: [this.invoice ? this.invoice.amount : null],
      minimumBid: [this.invoice ? this.invoice.minimumBid : null],
    });

    this.auditionForm.get('averagePaymentDelay').valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter(value => !!value || value === 0),
        filter(_ => !this.auditionForm.get('averagePaymentDelay').errors),
        tap(_ => this.calculateInvoiceBids()),
      )
      .subscribe();

    /*    if (this.data.action === 'edit') {
          this.minimumBidDiscount = this.calculateMinimumBid();
          this.sellNowDiscount = this.calculateSellNow();
        }*/
  }

  /* MAT STEPPER METHODS */

  goBack(): void {
    const step = this.stepper.selectedIndex;

    if (step < 3) {
      this.invoiceCreateOrUpdateErrorMessage = null;
      this.stepper.previous();
    }
  }

  goForward(): void {
    if (this.stepper.selectedIndex === 2) {
      if (this.data.action === 'add') {
        this.createInvoiceRequest();
      } else {
        this.editInvoiceRequest(false);
      }
    } else {
      this.stepper.next();
    }
  }

  onStepAnimationDone(): void {
    switch (this.stepper.selectedIndex) {
      case 0:
        this.getFocusOnFirstEnableControl(this.debtorForm);
        break;
      case 1:
        this.getFocusOnFirstEnableControl(this.invoiceForm);
        break;
      case 2:
        this.getFocusOnFirstEnableControl(this.auditionForm);
        break;
    }
  }

  onStepChange(event: StepperSelectionEvent): void {
    const previouslyStep = event.previouslySelectedIndex;
    const selectedStep = event.selectedIndex;

    if (selectedStep > previouslyStep) {
      if (selectedStep === 1) {
        if (this.data.action === 'add') {
          this.createDebtorRequest(true);
          this.auditionForm.get('averagePaymentDelay')
            .setValue(
              this.invoice ? this.invoice.averagePaymentDelay : this.averagePaymentDelayTmp || 0,
              { emitEvent: false },
            );
        } else if (this.data.action === 'edit') {
          this.invoiceForm.get('dueDate').markAsTouched();
        }
      } else if (selectedStep === 2) {
        this.auditionForm.get('invoiceAmount').setValue(this.currencyField.getNumber());

        this.calculateInvoiceBids();
      }
    }
  }

  calculateInvoiceBids(): void {
    this.auditionForm.get('minimumBid').disable();

    const invoiceBidsDto = {
      installments: this.buildInstallmentsPayload(),
      averagePaymentDelay: this.auditionForm.get('averagePaymentDelay').value,
      sellerTaxCodePrefix: this._mainService.user.taxCodePrefix,
      sellerTaxCode: this._mainService.user.taxCode,
      debtorTaxCodePrefix: this.debtorForm.get('taxCodePrefix').value,
      debtorTaxCode: this.debtorForm.get('taxCode').value,
    };

    this._invoiceService.getInvoiceBids(invoiceBidsDto)
      .pipe(
        tap((res: InvoiceBidsPrice) => {
          this.priceMin = res.minimumBid;
          this.priceSuggested = res.suggestedBid;
          this.priceMax = res.maximumBid;

          this.minimumBidDiscount = this.calculateMinimumBid(this.priceSuggested);

          this.auditionForm.get('minimumBid').setValidators([
            Validators.required,
            Validators.min(this.priceMin),
            Validators.max(this.priceMax),
          ]);

          this.auditionForm.get('minimumBid').setValue(this.priceSuggested);
          this.auditionForm.get('minimumBid').updateValueAndValidity();
          this.auditionForm.get('minimumBid').markAsTouched();
        }),
        finalize(() => this.auditionForm.get('minimumBid').enable()),
      )
      .subscribe();
  }

  getFocusOnFirstEnableControl(form: UntypedFormGroup) {
    for (const field in form.controls) {
      const control = form.get(field);

      if (control.enabled) {
        const controlName = this.getControlName(control);
        const element = document.getElementById(controlName);

        if (element !== null) {
          setTimeout(() => element.focus());
          break;
        }
      }
    }
  }

  getControlName(c: AbstractControl): string | null {
    const formGroup = c.parent.controls;
    return Object.keys(formGroup).find(name => c === formGroup[name]) || null;
  }

  /* / MAT STEPPER  METHODS */

  /* DEBTOR FORM METHODS */

  autocompleteFilterDebtors(value: ClientView | string): any[] {
    let toSearch = '';
    toSearch =
      typeof value === 'string'
        ? value.toLocaleLowerCase()
        : value.businessName.toLocaleLowerCase();

    return this.clients.filter(
      d => d.businessName.toLocaleLowerCase()
        .includes(toSearch) || d.taxCode.includes(toSearch),
    );
  }

  autocompleteDisplay(value: ClientView): string | undefined {
    return value ? `${value.businessName} - ${value.taxCodePrefix} ${value.taxCode}` : undefined;
  }

  autocompleteSelect(selectedEvent: MatAutocompleteSelectedEvent): void {
    if (selectedEvent.option.value) {
      this.userMadeAChoice = true;
      this.isNew = false;

      const selectedValue: ClientView = selectedEvent.option.value;

      this.getShowDebtorContactData(selectedValue);

      if (this.showDebtorContactData) {
        this.debtorForm.get('contactFirstName').setValue(selectedValue.lastInvoiceContactFirstName || selectedValue.contactFirstName);
        this.debtorForm.get('contactLastName').setValue(selectedValue.lastInvoiceContactLastName || selectedValue.contactLastName);
        this.debtorForm.get('contactEmail').setValue(selectedValue.lastInvoiceContactEmail || selectedValue.contactEmail);
        this.debtorForm.get('contactPhone').setValue(selectedValue.lastInvoiceContactPhone || selectedValue.contactPhone);
      }

      this.debtorForm.get('businessName').setValue(selectedValue.businessName);
      this.debtorForm.get('taxCode').setValue(selectedValue.taxCode);
      this.debtorForm.get('taxCodePrefix').setValue(selectedValue.taxCodePrefix);

      this.clientForm.get('clientPicker').disable();
      this.debtorForm.get('businessName').disable();
      this.debtorForm.get('taxCode').disable();
      this.debtorForm.get('taxCodePrefix').disable();

      this.averagePaymentDelayTmp = selectedValue.averagePaymentDelay;

      if (this.showDebtorContactData) {
        this.debtorForm.get('contactFirstName').updateValueAndValidity();
        this.debtorForm.get('contactLastName').updateValueAndValidity();
        this.debtorForm.get('contactEmail').updateValueAndValidity();
        this.debtorForm.get('contactPhone').updateValueAndValidity();
      }

      this.getFocusOnFirstEnableControl(this.debtorForm);
    }
  }

  getShowDebtorContactData(client: ClientView): void {
    const acceptedTransferType = client?.sellerDebtorTransferTypeAccepted;

    if (!acceptedTransferType) {
      this.showDebtorContactData = true;
    } else {
      const transferTypesWithoutDebtorContactData = [
        TransferType.ProSolutoNoConfirmNoNotification,
        TransferType.ProSolvendoNoConfirmNoNotification,
        TransferType.IasProSolutoNoConfirmNoNotification,
      ];

      this.showDebtorContactData = !transferTypesWithoutDebtorContactData.includes(acceptedTransferType);
    }

    if (this.showDebtorContactData) {
      this.setDebtorContactData();
    } else {
      this.debtorForm.removeControl('contactFirstName');
      this.debtorForm.removeControl('contactLastName');
      this.debtorForm.removeControl('contactEmail');
      this.debtorForm.removeControl('contactPhone');
    }
  }

  createNewDebtor(): void {
    this.userMadeAChoice = true;
    this.isNew = true;

    this.debtorForm.reset();
    this.debtorForm.get('businessName')
      .setValue(this.clientForm.get('clientPicker').value);
    this.debtorForm.get('taxCodePrefix')
      .setValue('IT');

    this.clientForm.reset();
    this.clientForm.get('clientPicker')
      .disable();
  }

  resetDebtorForm(): void {
    this.userMadeAChoice = false;
    this.isNew = false;

    this.clientForm.reset();
    this.clientForm.get('clientPicker')
      .enable();

    this.debtorForm.reset();
    this.debtorForm.get('businessName')
      .enable();
    this.debtorForm.get('taxCode')
      .enable();
    this.debtorForm.get('taxCodePrefix')
      .enable();
    this.debtorForm.removeControl('contactFirstName');
    this.debtorForm.removeControl('contactLastName');
    this.debtorForm.removeControl('contactEmail');
    this.debtorForm.removeControl('contactPhone');

    this.getShowDebtorContactData(this.data.client);
  }

  createDebtorRequest(dontNext?: boolean): void {
    if (!this.debtorForm.invalid) {
      if (!this.clientForm.get('clientPicker').value) {
        const form = this.debtorForm.getRawValue() as ClientView;
        this._clientService.save(form)
          .subscribe(
            _ => {
              if (!dontNext) {
                this.stepper.next();
              }
              this._messageService.showMessage(
                this._translateService.instant('INVOICE.SNACKBAR.DEBTOR_ADDED'),
              );
            },
            (err: FlamingoError) => {
              if (err.status === 409) {
                if (!dontNext) {
                  this.stepper.next();
                }
              } else {
                this._messageService.showMessage(
                  this._translateService.instant('INVOICE.SNACKBAR.INVOICE_ADDED'),
                );
                throw err;
              }
            },
          );
      } else {
        if (!dontNext) {
          this.stepper.next();
        }
      }
    }
  }

  /* / DEBTOR FORM METHODS */

  /* INVOICE FORM METHODS */

  onInvoiceFileChange(event): void {
    if (event.target.files && event.target.files.length) {
      this.invoiceFileName = event.target.files[0].name;
      this.invoiceFile = event.target.files[0];

      if (this.data.action === 'add') {
        this.invoiceForm.patchValue({
          file: this.invoiceFile,
        });
      }

      this.editInvoiceRequest(true, this.invoiceFile);
    }
  }

  viewInvoice(uuid: string): void {
    this._invoiceService.downloadInvoice(uuid)
      .subscribe(
        res => {
          this._httpService.openInBrowserFromAWSUrl(res);
        },
        err => {
          throw err;
        },
      );
  }

  viewAttachment(uuid: string, attachmentId: number): void {
    this._invoiceService.downloadAttachment(uuid, attachmentId)
      .subscribe(
        res => {
          this._httpService.openInBrowserFromAWSUrl(res);
        },
        err => {
          throw err;
        },
      );
  }

  /* / INVOICE FORM METHODS */

  /* AUCTION FORM METHODS */

  createInvoiceRequest(): void {
    if (!this.debtorForm.invalid && !this.invoiceForm.invalid && !this.auditionForm.invalid) {
      this.creatingInvoice = true;
      this.auditionForm.disable();

      const formData = new FormData();
      formData.append('number', this.invoiceForm.get('number').value);
      formData.append('minimumBid', this.auditionForm.get('minimumBid').value);
      formData.append('amount', this.currencyField.getNumber());
      formData.append(
        'issueDate',
        (this.invoiceForm.get('issueDate').value as Moment).format('YYYY-MM-DD'),
      );
      formData.append(
        'dueDate',
        (this.invoiceForm.get('dueDate').value as Moment).format('YYYY-MM-DD'),
      );
      formData.append('averagePaymentDelay', this.auditionForm.get('averagePaymentDelay').value);
      formData.append('debtorTaxCodePrefix', this.debtorForm.get('taxCodePrefix').value);
      formData.append('debtorTaxCode', this.debtorForm.get('taxCode').value);
      formData.append('debtorBusinessName', this.debtorForm.get('businessName').value);

      if (this.showDebtorContactData) {
        formData.append('debtorContactFirstName', this.debtorForm.get('contactFirstName').value);
        formData.append('debtorContactLastName', this.debtorForm.get('contactLastName').value);
        formData.append('debtorContactEmail', this.debtorForm.get('contactEmail').value);
        formData.append('debtorContactPhone', this.debtorForm.get('contactPhone').value);
      }

      this.appendInstallments(formData);

      // formData.append('invoiceFile', this.invoiceForm.get('file').value);

      this._invoiceService.create(formData)
        .subscribe(
          (res: Invoice) => {
            this.uuid = res.uuid;

            this.stepper._steps.toArray()[0].editable = false;
            this.stepper._steps.toArray()[1].editable = false;
            this.stepper._steps.toArray()[2].editable = false;

            this.creatingInvoice = false;
            this.auditionForm.enable();

            this.invoice = res;
            this.invoiceFiles = res.attachments;

            this._messageService.showMessage(
              this._translateService.instant('INVOICE.SNACKBAR.INVOICE_ADDED'),
            );
            this.invoiceCreateOrUpdateErrorMessage = null;
            this.stepper.next();
          },
          err => {
            this.creatingInvoice = false;
            this.auditionForm.enable();
            this.handleCreateOrUpdateInvoiceError(err);
            throw err;
          },
        );
    }
  }

  editInvoiceRequest(dontNext?: boolean, invoiceFile?: any): void {
    if (!this.debtorForm.invalid && !this.invoiceForm.invalid && !this.auditionForm.invalid) {
      if (!!invoiceFile) {
        this.uploadingAttachment = true;
      }
      this.creatingInvoice = true;
      this.auditionForm.disable();

      const formData = new FormData();
      formData.append('number', this.invoiceForm.get('number').value);
      formData.append('minimumBid', this.auditionForm.get('minimumBid').value);
      formData.append('amount', this.currencyField.getNumber());
      formData.append(
        'issueDate',
        moment(this.invoiceForm.get('issueDate').value)
          .format('YYYY-MM-DD'),
      );
      formData.append(
        'dueDate',
        moment(this.invoiceForm.get('dueDate').value)
          .format('YYYY-MM-DD'),
      );
      formData.append('averagePaymentDelay', this.auditionForm.get('averagePaymentDelay').value);
      formData.append('debtorTaxCodePrefix', this.debtorForm.get('taxCodePrefix').value);
      formData.append('debtorTaxCode', this.debtorForm.get('taxCode').value);
      formData.append('debtorBusinessName', this.debtorForm.get('businessName').value);

      if (this.showDebtorContactData) {
        formData.append('debtorContactFirstName', this.debtorForm.get('contactFirstName').value);
        formData.append('debtorContactLastName', this.debtorForm.get('contactLastName').value);
        formData.append('debtorContactEmail', this.debtorForm.get('contactEmail').value);
        formData.append('debtorContactPhone', this.debtorForm.get('contactPhone').value);
      } else {
        formData.append('debtorContactFirstName', this.invoice.debtorContactFirstName);
        formData.append('debtorContactLastName', this.invoice.debtorContactLastName);
        formData.append('debtorContactEmail', this.invoice.debtorContactEmail);
        formData.append('debtorContactPhone', this.invoice.debtorContactPhone);
      }

      if (!!invoiceFile) {
        this.invoiceForm.patchValue({
          file: invoiceFile,
        });
        formData.append('invoiceFile', this.invoiceForm.get('file').value);
      }

      this.appendInstallments(formData);

      this._invoiceService.edit(formData, this.invoice.uuid)
        .subscribe(
          _ => {
            this.stepper._steps.toArray()[0].editable = false;
            this.stepper._steps.toArray()[1].editable = false;
            this.stepper._steps.toArray()[2].editable = false;

            if (!!invoiceFile) {
              this.uploadingAttachment = false;
            }
            this.creatingInvoice = false;
            this.auditionForm.enable();

            if (!dontNext) {
              this.stepper.next();
            }
            this.invoiceCreateOrUpdateErrorMessage = null;
            this._messageService.showMessage(
              this._translateService.instant('INVOICE.SNACKBAR.INVOICE_EDITED'),
            );
          },
          err => {
            if (!!invoiceFile) {
              this.uploadingAttachment = false;
            }
            this.creatingInvoice = false;
            this.auditionForm.enable();
            this.handleCreateOrUpdateInvoiceError(err);
            throw err;
          },
        );
    }
  }

  findAttachment(type: AttachmentType): Attachment {
    return this.invoiceFiles.find(file => file.attachmentType === type);
  }

  /* / AUCTION FORM METHODS */

  isExpired(expirationDate: Date): boolean {
    return moment(expirationDate).isBefore(this.today, 'day');
  }

  editAttachment(event: any, attachmentId: number): void {
    this.uploadingAttachment = true;
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append('attachmentFile', file);

    this._invoiceService.updateAttachmentFile(formData, this.uuid, attachmentId)
      .subscribe(
        res => {
          const index = this.invoiceFiles.findIndex(f => f.id === res.id);
          if (index !== -1) {
            this.invoiceFiles.splice(index, 1, res);
          }
          this.uploadingAttachment = false;
        },
        () => {
          this.uploadingAttachment = false;
          this._messageService.showMessage(
            this._translateService.instant('INVOICE.SNACKBAR.ATTACHMENT_ERROR') + ': ' + file.name,
          );
        },
      );
  }

  attachmentInputLabel(type: string): string {
    switch (type) {
      case 'DELIVERY_NOTE':
        return '# DDT';
      case 'CUSTOMER_ORDER':
        return '# ORDINE';
      case 'SUPPLY_CONTRACT':
        return '# CONTRATTO';
      default:
        return null;
    }
  }

  showDeleteAttachment(file: Attachment): void {
    file.toDelete = !file.toDelete;
  }

  attachmentDelete(file: Attachment): void {
    this.uploadingAttachment = true;
    this._invoiceService.deleteAttachment(file, this.uuid)
      .subscribe(
        () => {
          this.invoiceFiles.splice(this.invoiceFiles.indexOf(file), 1);
          this.uploadingAttachment = false;
        },
        err => {
          this.uploadingAttachment = false;
          console.log(err);
          throw err;
        },
      );
  }

  checkCanEnd(): boolean {
    return this.invoiceFiles.length ? this.invoiceFiles.every(f => !!f.awsKey) : true;
  }

  closeDialog(): void {
    if (this.checkCanEnd()) {
      this._dialogRef.close({ refresh: true, publish: this.uuid });
    } else {
      this._messageService.showMessage('Devi caricare tutti i documenti.');
    }
  }

  ngAfterViewInit(): void {
    this.currencyField = new AutoNumeric('#amount', 'euro');

    if (this.data.action === 'edit') {
      this.getFocusOnFirstEnableControl(this.debtorForm);
    } else {
      this.getFocusOnFirstEnableControl(this.clientForm);
    }
  }

  isPublishButtonDisabled(): boolean {
    for (let file of this.invoiceFiles) {
      if (file.attachmentType === AttachmentType.client_ledger && this.isExpired(file.expirationDate)) {
        return true;
      }
    }

    return false;
  }

  isInvoiceFromXml(): boolean {
    return this.invoice && this.invoice.fromXml;
  }

  private buildInstallmentsPayload(): Array<{ amount: number; dueDate: string }> {
    if (this.isInvoiceFromXml() && this.invoiceInstallments?.length > 0) {
      return this.invoiceInstallments.map(installment => ({
        amount: installment.amount,
        dueDate: moment(installment.dueDate).format('YYYY-MM-DD'),
      }));
    }

    const amount = this.currencyField?.getNumber?.() ?? this.invoiceForm.get('amount').value;
    const dueDate = this.invoiceForm.get('dueDate').value;

    return [{
      amount: Number(amount),
      dueDate: moment(dueDate).format('YYYY-MM-DD'),
    }];
  }

  private appendInstallments(formData: FormData): void {
    formData.append('installments', JSON.stringify(this.buildInstallmentsPayload()));
  }

  private handleCreateOrUpdateInvoiceError(networkResponse: any): void {

    if (this.stepper.selectedIndex !== 2) {
      this._messageService.showMessage(
        this._translateService.instant('INVOICE.SNACKBAR.ERROR'),
      );
      return;
    }

    let errorBody = networkResponse.error;
    if ('message' in errorBody && 'messageIt' in errorBody) {
      this.invoiceCreateOrUpdateErrorMessage = this._translateService.currentLang === 'it' ? errorBody.messageIt : errorBody.message;
    } else {
      this._messageService.showMessage(
        this._translateService.instant('INVOICE.SNACKBAR.ERROR'),
      );
    }
  }

  private setDebtorContactData() {
    this.debtorForm.addControl('contactFirstName', new UntypedFormControl(
      this.invoice ? this.invoice.debtorContactFirstName : null,
      Validators.required,
    ));

    this.debtorForm.addControl('contactLastName', new UntypedFormControl(
      this.invoice ? this.invoice.debtorContactLastName : null,
      Validators.required,
    ));

    this.debtorForm.addControl('contactEmail', new UntypedFormControl(
      this.invoice ? this.invoice.debtorContactEmail : null,
      [Validators.required, Validators.email],
    ));

    this.debtorForm.addControl('contactPhone', new UntypedFormControl(
      this.invoice ? this.invoice.debtorContactPhone : null,
      [Validators.required, Validators.pattern('[0-9 ]+')],
    ));
  }
}

export interface NewInvoiceDialogModel {
  actionConfirm: string;
  actionCancel: string;
  action: string;
  invoice: Invoice;
  title: string;
  attachment?: boolean;
  client: ClientView;
}
